import * as Yup from 'yup'
import { REQUIRED_FIELD } from '@constants'
import { getCurrentYear } from '@utils/basic'
import moment from 'moment'
import { dateExistValidation } from '../../models/validation'

export interface GetYearsPayload {
  assetCompanyID: number
}

export interface FetchPeriodsPayload {
  assetCompanyID: number
  year: number
}

export const periodSchema = (allowWeekends: boolean) =>
  Yup.object({
    AssetCompanyID: Yup.number()
      .typeError(REQUIRED_FIELD)
      .required(REQUIRED_FIELD)
      .default(100),
    CP_YEAR: Yup.number()
      .typeError(REQUIRED_FIELD)
      .required(REQUIRED_FIELD)
      .default(getCurrentYear()),
    CP_PERIOD: Yup.number().typeError(REQUIRED_FIELD).required(REQUIRED_FIELD).default(1),
    CP_START_DATE: Yup.string()
    .typeError(REQUIRED_FIELD)
    .required(REQUIRED_FIELD)
    .default(''),
    CP_DATE: Yup.string()
      .typeError(REQUIRED_FIELD)
      .required(REQUIRED_FIELD)
      .default('')
      .test('allowWeekendsTest', 'Weekends are not allowed', date => {
        if (allowWeekends) return true
        const dayOfWeek = moment(date).weekday()
        return !(dayOfWeek === 0 || dayOfWeek === 6)
      })
      .test(dateExistValidation), 
    CP_PERCENT: Yup.number().typeError(REQUIRED_FIELD).required(REQUIRED_FIELD).default(0),
    CP_FRWD: Yup.bool().default(false),
    CP_WEEKEND: Yup.bool().default(true),
    CP_YTD_PCT: Yup.number().required(REQUIRED_FIELD),
  })
export type Period = Yup.InferType<ReturnType<typeof periodSchema>>
export type PeriodKey = keyof Period

export interface EditPeriodsPayload {
  Periods: Period[]
  AllowWeekends: boolean
}

export const editPeriodsSchema = Yup.object({
  Periods: Yup.array().when('AllowWeekends', {
    is: true,
    then: Yup.array().of(periodSchema(true)),
    otherwise: Yup.array().of(periodSchema(false)),
  }),
  AllowWeekends: Yup.bool().default(true),
})

export const generatePeriodSchema = (companyHasPeriods: boolean, years: number[]) => {
  const sortedYears = companyHasPeriods ? [...years].sort((a, b) => a - b) : []
  const minExisting = sortedYears[0]
  const maxExisting = sortedYears[sortedYears.length - 1]

  return Yup.object({
    AssetCompanyID: Yup.number().required(REQUIRED_FIELD).default(100),
    FromYear: Yup.number()
      .typeError(REQUIRED_FIELD)
      .required(REQUIRED_FIELD)
      .min(1900, 'Enter a valid year')
      .max(2999, 'Enter a valid year')
      .test({
        test: function isFromYearBeforeOrEqualToToYear(FromYear) {
          if (FromYear === undefined || this.parent.ToYear === undefined) return true
          return FromYear <= this.parent.ToYear
        },
        message: 'Must be less than or equal to up to year.',
      })
      .default(getCurrentYear()),

    ToYear: Yup.number()
      .typeError(REQUIRED_FIELD)
      .required(REQUIRED_FIELD)
      .min(1900, 'Enter a valid year')
      .max(2999, 'Enter a valid year')
      .test({
        test: function isToYearAfterFromYear(ToYear) {
          if (ToYear === undefined || this.parent.FromYear === undefined) return true
          return this.parent.FromYear <= ToYear
        },
        message: 'Must be greater than or equal to from year.',
      })
      .test({
        test: function rangeWithin20Years(ToYear) {
          const FromYear = this.parent.FromYear
          if (ToYear === undefined || FromYear === undefined || FromYear > ToYear)
            return true

          const existingInRange = sortedYears.filter(
            y => y >= FromYear && y <= ToYear
          ).length
          const newYears = ToYear - FromYear + 1 - existingInRange
          return newYears <= 20
        },
        message: 'You can only generate periods for 20 years at a time.',
      })
      .test({
        name: 'noOverlap',
        test: function noOverlap(ToYear) {
          const FromYear = this.parent.FromYear
          if (!companyHasPeriods || ToYear === undefined || FromYear === undefined)
            return true

          const overlap = sortedYears.filter(y => y >= FromYear && y <= ToYear)
          if (overlap.length === 0) return true

          return this.createError({
            message: `Years ${overlap.join(
              ', '
            )} already exist. Choose a range outside existing periods.`,
          })
        },
      })
      .test({
        name: 'noGap',
        test: function noGap(ToYear) {
          const FromYear = this.parent.FromYear
          if (!companyHasPeriods || ToYear === undefined || FromYear === undefined)
            return true

          if (FromYear <= maxExisting + 1 && ToYear >= minExisting - 1) return true

          return this.createError({
            message: `Range must touch the existing range (${minExisting}-${maxExisting}).`,
          })
        },
      })
      .default(getCurrentYear()),
    AllowWeekends: Yup.bool().required(REQUIRED_FIELD).default(false),
  })
}
export interface GeneratePeriodsParams {
  AssetCompanyID: number
  FromYear: number
  ToYear: number
  AllowWeekends: boolean
}

export type GeneratePeriodParamKey = keyof GeneratePeriodsParams
